/**
 * src/services/pushNotifications.ts
 *
 * Frontend service for Web Push Notifications.
 * Handles service worker registration, permission requests,
 * and subscribing/unsubscribing from the backend.
 */

// CRA replaces process.env.REACT_APP_* at build time — must reference directly,
// not via dynamic property access like (process.env as any)["KEY"] which bypasses it.
const VAPID_PUBLIC_KEY: string = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert a VAPID base64 public key to Uint8Array for the browser API */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; i++) {
    view[i] = rawData.charCodeAt(i);
  }
  return view;
}

// ─── Feature detection ────────────────────────────────────────────────────────

/** Returns true if this browser supports Web Push */
export function isPushNotificationSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Returns the current notification permission state */
export function getNotificationPermission(): NotificationPermission {
  if (!("Notification" in window)) return "denied";
  return Notification.permission;
}

// ─── Service Worker ───────────────────────────────────────────────────────────

let swRegistration: ServiceWorkerRegistration | null = null;

/** Register the service worker and wait until it is active */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;

  try {
    // register() kicks off installation but the SW isn't usable for push yet
    await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    // .ready resolves only once a SW is fully active — required for pushManager.subscribe()
    const reg = await navigator.serviceWorker.ready;
    swRegistration = reg;
    console.log("[Push] Service worker active:", reg.scope);
    return reg;
  } catch (err) {
    console.error("[Push] Service worker registration failed:", err);
    return null;
  }
}

/** Get the active service worker registration */
async function getSwRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (swRegistration) return swRegistration;
  if (!("serviceWorker" in navigator)) return null;
  try {
    swRegistration = await navigator.serviceWorker.ready;
    return swRegistration;
  } catch {
    return null;
  }
}

// ─── Subscribe ────────────────────────────────────────────────────────────────

/**
 * Request notification permission, create a Push subscription,
 * and register it with the backend.
 *
 * @param subscribeUrl  Backend endpoint e.g. "https://api.example.com/api/push/subscribe"
 * @param authToken     JWT bearer token for the authenticated user
 * @returns true on success, false on failure/denial
 */
export async function initializePushNotifications(
  subscribeUrl: string,
  authToken?: string,
): Promise<boolean> {
  if (!isPushNotificationSupported()) {
    console.warn("[Push] Not supported in this browser");
    return false;
  }

  if (!VAPID_PUBLIC_KEY) {
    console.error(
      "[Push] VAPID_PUBLIC_KEY is not set in environment variables",
    );
    return false;
  }

  // 1. Request permission
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    console.log("[Push] Permission denied:", permission);
    return false;
  }

  // 2. Get service worker
  const reg = await getSwRegistration();
  if (!reg) {
    console.error("[Push] No service worker registration found");
    return false;
  }

  // 3. Check for existing subscription or create new one
  let subscription = await reg.pushManager.getSubscription();

  if (!subscription) {
    try {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          VAPID_PUBLIC_KEY,
        ) as unknown as ArrayBuffer,
      });
    } catch (err) {
      console.error("[Push] Failed to create subscription:", err);
      return false;
    }
  }

  // 4. Send subscription to backend
  try {
    const token = authToken || localStorage.getItem("token") || "";
    const res = await fetch(subscribeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(
            String.fromCharCode(
              ...Array.from(new Uint8Array(subscription.getKey("p256dh")!)),
            ),
          ),
          auth: btoa(
            String.fromCharCode(
              ...Array.from(new Uint8Array(subscription.getKey("auth")!)),
            ),
          ),
        },
      }),
    });

    if (!res.ok) {
      console.error("[Push] Backend subscription failed:", await res.text());
      return false;
    }

    console.log("[Push] Subscribed successfully");
    return true;
  } catch (err) {
    console.error("[Push] Failed to register subscription with backend:", err);
    return false;
  }
}

// ─── Unsubscribe ─────────────────────────────────────────────────────────────

/**
 * Unsubscribe from push notifications and notify the backend.
 */
export async function unsubscribeFromPushNotifications(
  unsubscribeUrl: string,
  authToken?: string,
): Promise<boolean> {
  const reg = await getSwRegistration();
  if (!reg) return false;

  const subscription = await reg.pushManager.getSubscription();
  if (!subscription) return true; // already unsubscribed

  // Notify backend first
  try {
    const token = authToken || localStorage.getItem("token") || "";
    await fetch(unsubscribeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    });
  } catch (err) {
    console.warn("[Push] Failed to notify backend of unsubscribe:", err);
  }

  // Unsubscribe from browser
  await subscription.unsubscribe();
  console.log("[Push] Unsubscribed");
  return true;
}

// ─── Status check ─────────────────────────────────────────────────────────────

/**
 * Returns true if the user is currently subscribed to push notifications.
 */
export async function isSubscribed(): Promise<boolean> {
  const reg = await getSwRegistration();
  if (!reg) return false;
  const subscription = await reg.pushManager.getSubscription();
  return !!subscription && Notification.permission === "granted";
}

// ─── Browser-side only subscription ──────────────────────────────────────────
/**
 * Requests notification permission and returns the raw PushSubscription object.
 * Does NOT send anything to the backend — use your app's HTTP client for that.
 * Returns null if permission denied or SW not available.
 */
export async function requestPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushNotificationSupported()) {
    console.warn("[Push] Not supported in this browser");
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    console.log("[Push] Permission not granted:", permission);
    return null;
  }

  const reg = await getSwRegistration();
  if (!reg) {
    console.error("[Push] Service worker not ready");
    return null;
  }

  // Return existing subscription if still valid
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    console.log("[Push] Reusing existing subscription");
    return existing;
  }

  // Need VAPID public key to create a new subscription
  if (!VAPID_PUBLIC_KEY) {
    console.error(
      "[Push] VAPID_PUBLIC_KEY is not set in frontend env (REACT_APP_VAPID_PUBLIC_KEY / VITE_VAPID_PUBLIC_KEY)",
    );
    throw new Error("VAPID_PUBLIC_KEY not configured");
  }

  console.log("[Push] Creating new subscription...");
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(
      VAPID_PUBLIC_KEY,
    ) as unknown as ArrayBuffer,
  });
}

// ─── PWA Install prompt ───────────────────────────────────────────────────────
// Captures the browser's beforeinstallprompt event so you can trigger
// the "Add to Home Screen" prompt manually (e.g. from a button).
// Call setupInstallPrompt() once on app load (e.g. in App.tsx useEffect).

let deferredInstallPrompt: any = null;

export function setupInstallPrompt(): void {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // stop the automatic mini-infobar on mobile
    deferredInstallPrompt = e;
  });
}

/**
 * Show the "Add to Home Screen" install prompt.
 * Returns true if the user accepted, false if dismissed.
 * Returns null if no prompt is available (already installed, or not supported).
 */
export async function showInstallPrompt(): Promise<boolean | null> {
  if (!deferredInstallPrompt) return null;
  deferredInstallPrompt.prompt();
  const { outcome } = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  return outcome === "accepted";
}

/** Returns true if the install prompt is available to show */
export function canShowInstallPrompt(): boolean {
  return !!deferredInstallPrompt;
}
